import { FunctionComponent, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { EntityResponse, useClientEntities } from "../../api";
import {
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    Divider,
    Flex,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Spacer,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { EntityTree } from "./components/EntityTree";
import { IconAlertTriangle, IconArrowsMoveVertical, IconBuilding, IconChevronRight, IconFolder, IconFold, IconPlus, IconSearch, IconSettings } from "@tabler/icons-react";
import { EntityTreeContextProvider, useEntityTreeContext } from "./components/EntityTreeContext";
import { SetPageTitle } from "../../components/SetPageTitle";
import { EntityAddDialog } from "./components/EntityAddDialog/EntityAddDialog";
import { useAreaPermissions } from "../../hooks";
import { EntitiesArea } from "../../auth";
import { sortBy } from "lodash";
import { EntityOverviewTab } from "./components/EntityOverviewTab";
import { EntitySettingsTab } from "./components/EntitySettingsTab";
import { SurveyLinkSection } from "./components/SurveyLinkSection/SurveyLinkSection";
import { EntityAddressTab } from "./components/EntityAddressTab";
import { EntityContactTab } from "./components/EntityContactTab";

export const ClientEntitiesPage: FunctionComponent = () => {
    const { cId } = useParams<keyof { cId: string }>();

    return (
        <>
            <SetPageTitle title={"Client Entities Page"} />
            {cId && <EntityManagerWrapper clientId={cId} />}
        </>
    );
};

const EntityManagerWrapper: FunctionComponent<{ clientId: string }> = ({ clientId }) => {
    const entities = useClientEntities(clientId),
        expanded = useDisclosure();

    return (
        <EntityTreeContextProvider
            entities={entities.data || []}
            isEditable={true}
            isLoading={entities.isInitialLoading}
            isExpanded={expanded.isOpen}
            onExpandToggle={expanded.onToggle}
            onRefetch={async () => {
                await entities.refetch();
            }}
        >
            {entities.data ? <EntityManager clientId={clientId} entities={entities.data} /> : <Spinner />}
        </EntityTreeContextProvider>
    );
};

const EntityManager: FunctionComponent<{ clientId: string; entities: EntityResponse[] }> = ({ clientId }) => {
    const { onAdd, onExpandAllToggle, isExpandAll } = useEntityTreeContext();
    const { selectedEntity, pathToSelected, isAdding, onAddClose, setSelectedEntityId } = useEntityTreeContext();
    const areaPermissions = useAreaPermissions(EntitiesArea, clientId);

    const [filterTerm, setFilterTerm] = useState("");

    // Tab URL sync — slugs differ by entity type
    const [searchParams, setSearchParams] = useSearchParams();
    const leafTabSlugs = ["overview", "address", "contact", "settings"];
    const dirTabSlugs = ["overview", "contents", "address", "contact", "settings"];

    const tabSlugs = selectedEntity?.entityType === "Leaf" ? leafTabSlugs : dirTabSlugs;
    const tabParam = searchParams.get("etab") ?? "";
    const tabIndex = tabSlugs.indexOf(tabParam) >= 0 ? tabSlugs.indexOf(tabParam) : 0;

    const handleTabChange = (index: number) => {
        setSearchParams(
            prev => {
                prev.set("etab", tabSlugs[index]);
                return prev;
            },
            { replace: true }
        );
    };

    const navigateToSettings = () => handleTabChange(tabSlugs.indexOf("settings"));

    // Ancestor breadcrumb — all ancestors except the selected entity itself
    const ancestorPath = pathToSelected
        .slice(0, -1)
        .map(e => e.name)
        .join(" › ");

    return (
        <>
            <SetPageTitle title={"Manage Entities"} />

            <Grid templateColumns={"360px 1fr"} gap={"1.5rem"} alignItems={"start"}>
                {/* ── Left: Tree Panel ───────────────────────────────────── */}
                <Card variant={"withShadow"}>
                    <CardBody p={"1.25rem"}>
                        <VStack align={"stretch"} spacing={"1rem"}>
                            {/* Header */}
                            <HStack justify={"space-between"}>
                                <Heading size={"md"} m={0}>
                                    Entity Tree
                                </Heading>
                                <IconButton
                                    onClick={onExpandAllToggle}
                                    aria-label={isExpandAll ? "Collapse all" : "Expand all"}
                                    icon={<Icon as={isExpandAll ? IconFold : IconArrowsMoveVertical} />}
                                    size={"sm"}
                                    variant={"ghost"}
                                />
                            </HStack>

                            {/* Search */}
                            <InputGroup size={"sm"}>
                                <InputLeftElement pointerEvents={"none"}>
                                    <Icon as={IconSearch} color={"gray.400"} boxSize={"1rem"} />
                                </InputLeftElement>
                                <Input placeholder={"Filter entities…"} value={filterTerm} onChange={e => setFilterTerm(e.target.value)} borderRadius={"md"} />
                            </InputGroup>

                            {/* Add to root */}
                            <Button
                                variant={"ghost"}
                                size={"sm"}
                                onClick={() => onAdd()}
                                rightIcon={<Icon as={IconPlus} />}
                                fontWeight={"normal"}
                                justifyContent={"flex-start"}
                            >
                                Add to Root
                            </Button>

                            <Divider />

                            {/* Tree */}
                            <Box overflowY={"auto"} maxH={"calc(100vh - 280px)"}>
                                <EntityTree filterTerm={filterTerm} />
                            </Box>
                        </VStack>
                    </CardBody>
                </Card>

                {/* ── Right: Detail Panel ─────────────────────────────────── */}
                {selectedEntity ? (
                    <VStack align={"stretch"} spacing={"1.25rem"}>
                        {/* Summary card */}
                        <Card variant={"withShadow"}>
                            <CardBody p={"1.5rem"}>
                                <HStack justify={"space-between"} align={"start"}>
                                    <VStack align={"start"} spacing={1}>
                                        <HStack>
                                            <Icon
                                                as={selectedEntity.entityType === "Directory" ? IconFolder : IconBuilding}
                                                color={"gray.500"}
                                                boxSize={"1.25rem"}
                                            />
                                            <Heading size={"lg"} m={0}>
                                                {selectedEntity.name}
                                            </Heading>
                                            <Badge colorScheme={selectedEntity.entityType === "Leaf" ? "blue" : "gray"} borderRadius={"full"} px={2}>
                                                {selectedEntity.entityType === "Leaf" ? "Location" : "Folder"}
                                            </Badge>
                                        </HStack>
                                        {ancestorPath && (
                                            <Text fontSize={"sm"} color={"gray.500"}>
                                                {ancestorPath}
                                            </Text>
                                        )}
                                    </VStack>
                                    {areaPermissions.hasAny(EntitiesArea.update, EntitiesArea.delete) && (
                                        <IconButton
                                            icon={<Icon as={IconSettings} />}
                                            aria-label={"Entity settings"}
                                            onClick={navigateToSettings}
                                            variant={"ghost"}
                                        />
                                    )}
                                </HStack>

                                {selectedEntity.entityType === "Leaf" && (
                                    <>
                                        <Divider my={"1rem"} />
                                        <Grid gridTemplateColumns={"auto 1fr auto 1fr"} columnGap={"1rem"} rowGap={".25rem"} alignItems={"center"}>
                                            {selectedEntity.ein && (
                                                <>
                                                    <GridItem>
                                                        <Text variant={"label"}>EIN:</Text>
                                                    </GridItem>
                                                    <GridItem>{selectedEntity.ein}</GridItem>
                                                </>
                                            )}
                                            {selectedEntity.phone && (
                                                <>
                                                    <GridItem>
                                                        <Text variant={"label"}>Phone:</Text>
                                                    </GridItem>
                                                    <GridItem>{selectedEntity.phone}</GridItem>
                                                </>
                                            )}
                                            <GridItem>
                                                <Text variant={"label"}>Address:</Text>
                                            </GridItem>
                                            <GridItem>
                                                {selectedEntity.street1 ? (
                                                    [
                                                        selectedEntity.street1,
                                                        selectedEntity.street2,
                                                        selectedEntity.city,
                                                        selectedEntity.state,
                                                        selectedEntity.zipCode,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(", ")
                                                ) : (
                                                    <HStack spacing={1} color={"orange.500"}>
                                                        <Icon as={IconAlertTriangle} boxSize={"0.875rem"} />
                                                        <Text fontSize={"sm"}>Address not configured — employer address will be blank on generated PDF forms</Text>
                                                    </HStack>
                                                )}
                                            </GridItem>
                                        </Grid>
                                    </>
                                )}
                                {/* Survey link — always visible for leaf entities */}
                                {selectedEntity.entityType === "Leaf" && (
                                    <>
                                        <Divider my={"1rem"} />
                                        <SurveyLinkSection clientId={clientId} entityId={selectedEntity.id} />
                                    </>
                                )}
                            </CardBody>
                        </Card>

                        {/* ── Leaf entity tabs ─────────────────────────────── */}
                        {selectedEntity.entityType === "Leaf" && (
                            <Tabs variant={"enclosed"} colorScheme={"blue"} index={tabIndex} onChange={handleTabChange}>
                                <TabList>
                                    <Tab>Overview</Tab>
                                    <Tab>Address</Tab>
                                    <Tab>Contact</Tab>
                                    <Tab>Settings</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel px={0}>
                                        <EntityOverviewTab clientId={clientId} entity={selectedEntity} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <EntityAddressTab clientId={clientId} entityId={selectedEntity.id} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <EntityContactTab clientId={clientId} entityId={selectedEntity.id} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <Card variant={"withShadow"}>
                                            <CardBody p={"1.5rem"}>
                                                <EntitySettingsTab clientId={clientId} entityId={selectedEntity.id} />
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )}

                        {/* ── Directory entity tabs ─────────────────────────── */}
                        {selectedEntity.entityType === "Directory" && (
                            <Tabs variant={"enclosed"} colorScheme={"blue"} index={tabIndex} onChange={handleTabChange}>
                                <TabList>
                                    <Tab>Overview</Tab>
                                    <Tab>Contents</Tab>
                                    <Tab>Address</Tab>
                                    <Tab>Contact</Tab>
                                    <Tab>Settings</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel px={0}>
                                        <EntityOverviewTab clientId={clientId} entity={selectedEntity} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <Card variant={"withShadow"}>
                                            <CardBody p={"1.5rem"}>
                                                <HStack justify={"space-between"} mb={"1rem"}>
                                                    <Heading variant={"info"} m={0}>
                                                        Contents
                                                    </Heading>
                                                    <Button
                                                        size={"sm"}
                                                        variant={"ghost"}
                                                        rightIcon={<Icon as={IconPlus} />}
                                                        fontWeight={"normal"}
                                                        onClick={() => onAdd(selectedEntity.id)}
                                                    >
                                                        Add child
                                                    </Button>
                                                </HStack>
                                                {selectedEntity.children.length === 0 ? (
                                                    <Text color={"gray.400"} fontSize={"sm"}>
                                                        This folder is empty.
                                                    </Text>
                                                ) : (
                                                    <VStack align={"stretch"} spacing={0} divider={<Divider />}>
                                                        {sortBy(selectedEntity.children, c => (c.entityType === "Directory" ? `A${c.name}` : c.name)).map(
                                                            child => (
                                                                <HStack
                                                                    key={child.id}
                                                                    px={".75rem"}
                                                                    py={".625rem"}
                                                                    cursor={"pointer"}
                                                                    _hover={{ bg: "gray.50" }}
                                                                    borderRadius={"sm"}
                                                                    onClick={() => setSelectedEntityId(child.id)}
                                                                >
                                                                    <Icon
                                                                        as={child.entityType === "Directory" ? IconFolder : IconBuilding}
                                                                        color={"gray.500"}
                                                                        boxSize={"1rem"}
                                                                    />
                                                                    <Text fontWeight={"medium"}>{child.name}</Text>
                                                                    <Badge
                                                                        colorScheme={child.entityType === "Leaf" ? "blue" : "gray"}
                                                                        variant={"subtle"}
                                                                        borderRadius={"full"}
                                                                    >
                                                                        {child.entityType === "Leaf" ? "Location" : "Folder"}
                                                                    </Badge>
                                                                    <Spacer />
                                                                    {child.entityType === "Leaf" && child.candidateCount > 0 && (
                                                                        <Badge colorScheme={"blue"} borderRadius={"full"}>
                                                                            {child.candidateCount} candidates
                                                                        </Badge>
                                                                    )}
                                                                    <Icon as={IconChevronRight} color={"gray.400"} boxSize={".875rem"} />
                                                                </HStack>
                                                            )
                                                        )}
                                                    </VStack>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <EntityAddressTab clientId={clientId} entityId={selectedEntity.id} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <EntityContactTab clientId={clientId} entityId={selectedEntity.id} />
                                    </TabPanel>
                                    <TabPanel px={0}>
                                        <Card variant={"withShadow"}>
                                            <CardBody p={"1.5rem"}>
                                                <EntitySettingsTab clientId={clientId} entityId={selectedEntity.id} />
                                            </CardBody>
                                        </Card>
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        )}
                    </VStack>
                ) : (
                    /* Empty state */
                    <Flex direction={"column"} align={"center"} justify={"center"} minH={"300px"} gap={"0.75rem"} color={"gray.400"}>
                        <Icon as={IconBuilding} boxSize={12} />
                        <Text fontSize={"lg"} fontWeight={"medium"}>
                            Select an entity
                        </Text>
                        <Text fontSize={"sm"}>Choose a location or folder from the tree to view its details.</Text>
                    </Flex>
                )}
            </Grid>

            <EntityAddDialog isOpen={isAdding} clientId={clientId} onClose={onAddClose} selectedEntity={selectedEntity} />
        </>
    );
};
